import type { IBasketItem } from '../context/BasketStore'
import type { IProduct } from '../context/ProductDataStore'
import { fetchRepairPartDetail } from './parts'
import { needsPartRecovery, partRecoveryKey, findRecoveryPart } from '../utils/basketRecovery'
import { getPartPrice, getPartStockLimits, getPartRepairImage } from '../utils/repairParts'

const pending = new Map<string, Promise<IBasketItem[]>>()

export const recoverSavedParts = (items: IBasketItem[], region: string): Promise<IBasketItem[]> => {
  const damaged = items.filter(needsPartRecovery)
  if (!damaged.length) return Promise.resolve([])
  const key = `${region.toLowerCase()}|${partRecoveryKey(items)}`
  const existing = pending.get(key)
  if (existing) return existing

  const recovery = (async () => {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL!.replace(/\/$/, '')}/payments/checkout/recover-parts/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: damaged.map(item => ({
        productItemId: item.colourWay._id, quantity: item.quantity ?? 1,
        components: [{ partId: item.colourWay.components![0].componentVariantId,
          chosenMaterial: item.colourWay.components![0].chosenMaterial }],
      })) }),
    })
    if (!response.ok) throw new Error('Saved parts cannot be uniquely recovered')
    const resolved = (await response.json()).items
    if (!Array.isArray(resolved) || resolved.length !== damaged.length) throw new Error('Incomplete recovery')
    return Promise.all(damaged.map(async (item, index) => {
      const detail = await fetchRepairPartDetail(resolved[index].productItemId, undefined, region)
      // Recheck the current product response, including its allowed colours.
      const product = detail?.product
      if (!product || !findRecoveryPart(item, [product])) throw new Error('Saved part is unavailable')
      const price = getPartPrice(detail)
      if (!product.price || !Number.isFinite(price) || price <= 0) throw new Error('Saved part has no current price')
      const render = getPartRepairImage(detail, item.colourWay.components![0].chosenMaterial.$oid)
      return {
        ...item,
        name: product.productName,
        price,
        colourWay: {
          ...product,
          productName: product.productName,
          group: 'repair-part',
          components: item.colourWay.components,
          stockLimits: getPartStockLimits(detail),
          ...(render ? { imageUrl: render.url, heroImage: { url: render.url, showThumbnail: true } } : {}),
        } as IProduct,
      }
    }))
  })()
  pending.set(key, recovery)
  void recovery.finally(() => pending.delete(key)).catch(() => {})
  return recovery
}
